import HChart from '@components/HChart/HChart';
import { type FC, useCallback, useEffect, useMemo, useState } from 'react';
import SankeyModule from 'highcharts/modules/sankey';
import type Highcharts from 'highcharts';
import { type BuildingSankey } from 'interfaces/assets/Building.interface';
import { BsThreeDots } from 'react-icons/bs';
import { MdOutlineRemove } from 'react-icons/md';
import { useTheme } from 'context/ThemeProvider/ThemeProvider';
import {
  convertToPercentageParsedString,
  formatDecimalSeparator,
} from 'utils/formatters/number/numberUtils';
import { PiArrowDownFill, PiArrowUpFill } from 'react-icons/pi';
import {
  type SankeyLinks,
  type SankeyNodes,
} from 'interfaces/sankey/Sankey.interface';

interface BuildingSankeyChartProps {
  data?: BuildingSankey;
  unit: string;
  totalConsumptionEnergy?: number;
  nodeOnClick: ({
    meterId,
    meterName,
  }: {
    meterId: number;
    meterName: string;
  }) => void;
}
interface SankeyPoint {
  fromNode?: {
    meter_category: string;
    name: string;
  };
  toNode?: {
    name: string;
    meter_category: string;
  };
  name?: string;
  weight?: number;
  sum?: number;
}

interface CustomNodePoint extends Highcharts.Point {
  custom?: {
    meterId?: number;
  };
}

interface CustomSankeyNode extends SankeyNodes {
  custom?: {
    meterId?: number;
  };
}

type HiddenConnectionsType = Record<string, SankeyLinks[]>;

const getNodeColor = (color: string, category?: number | string): string => {
  if (category === 'Non-measured consumption') {
    return '#D9D9D9';
  }
  return color === 'green' ? '#8CD134' : '#E92252';
};

const BuildingSankeyChart: FC<BuildingSankeyChartProps> = ({
  data,
  unit,
  totalConsumptionEnergy,
  nodeOnClick,
}) => {
  const { theme } = useTheme();
  const [expandedNode, setExpandedNode] = useState<string | undefined>(
    undefined
  );
  const [chart, setChart] = useState<Highcharts.Chart | null>(null);

  const [nodePositions, setNodePositions] = useState<
    Record<string, { x: number; y: number }>
  >({});
  const [everyNodePositions, setEveryNodePositions] = useState<
    Record<string, { x: number; y: number }>
  >({});

  const parseLinks = (links?: SankeyLinks[]): any[] => {
    if (!links) return [];
    return links?.map((link) => ({
      ...link,
      from: link.from_node,
    }));
  };

  const hiddenConnections = useMemo<HiddenConnectionsType | undefined>(() => {
    if (!data) return {};
    const highestLevelNode = data.highest_level_node;
    if (highestLevelNode <= 1) {
      return {};
    }
    const highest = parseLinks(data?.links)?.filter(
      (item) => (item.level || 0) === highestLevelNode
    );
    if (highest) {
      const groupedByParent = highest.reduce(
        (acc: HiddenConnectionsType, link) => {
          const parentId = link.from_node;
          if (!acc[parentId]) {
            acc[parentId] = [];
          }
          acc[parentId].push(link);
          return acc;
        },
        {}
      );
      return groupedByParent;
    }
  }, [data]);

  const visibleData = useMemo(() => {
    if (!data) {
      return [];
    }
    const highestLevelNode = data.highest_level_node;
    const parsedLevelNode = highestLevelNode > 1 ? highestLevelNode : 100;

    const sankeyData =
      parseLinks(data?.links)
        ?.filter((item) => (item.level || 0) < parsedLevelNode)
        ?.sort((a, b) => b.weight - a.weight) ?? [];

    if (expandedNode && hiddenConnections?.[expandedNode] && sankeyData) {
      const filteredData = sankeyData.filter(
        (node) => node.from_node !== expandedNode && node.to !== null
      );
      const filteredHiddenConnections =
        hiddenConnections[expandedNode].sort((a, b) => b.weight - a.weight) ??
        [];

      const finalLinks = [...filteredData, ...filteredHiddenConnections];

      return finalLinks;
    }

    return sankeyData;
  }, [data?.links, hiddenConnections, expandedNode]);

  const updateChartData = useCallback(() => {
    if (chart) {
      chart.series[0].setData(visibleData);
    }
  }, [data, visibleData]);

  const handleExpandNode = (nodeId: string): void => {
    if (expandedNode === nodeId) {
      // Collapse if clicking the same node
      setExpandedNode(undefined);
    } else {
      // Expand new node (this automatically collapses any previously expanded node)
      setExpandedNode(nodeId);
    }
    updateChartData();
  };

  const chartNodes = useMemo(() => {
    if (data?.nodes) {
      const nodes: CustomSankeyNode[] = data.nodes.map((node) => {
        const customNode: any = {
          ...node,
          id: node.target_id,
          name: node.meter_name,
          color: getNodeColor(node.color, node.meter_category),
          custom: {
            meterId: node.meter_id,
          },
        };

        // For GRID nodes, try to override node properties for proper visual sizing
        if (node.meter_category === 'GRID') {
          const sourceLink = visibleData.find(
            (link: any) =>
              link.to === node.target_id && link.meter_category === 'SOURCE'
          );
          if (sourceLink) {
            // Try multiple properties that might control node size
            customNode.sum = sourceLink.weight;
            customNode.value = sourceLink.weight;
            customNode.y = sourceLink.weight;
            customNode.weight = sourceLink.weight;
          }
        }

        // For SOURCE nodes (SOLAR, WIND, CHP), use the outgoing link weight
        if (['SOLAR', 'WIND', 'CHP'].includes(node.meter_category)) {
          const outgoingLink = visibleData.find(
            (link: any) =>
              link.from_node === node.target_id &&
              link.meter_category === node.meter_category
          );
          if (outgoingLink) {
            // Override node size properties with outgoing link weight
            customNode.sum = outgoingLink.weight;
            customNode.value = outgoingLink.weight;
            customNode.y = outgoingLink.weight;
            customNode.weight = outgoingLink.weight;
          }
        }

        // For TOTAL CONSUMPTION nodes, store the actual energy_use from backend
        // Highcharts will calculate based on links, but we'll override in tooltip
        if (node.meter_category === 'TOTAL CONSUMPTION') {
          // Store the actual consumption value from the backend data
          customNode.actualEnergy = totalConsumptionEnergy;
        }

        return customNode;
      });
      return nodes;
    }
    return [];
  }, [data, hiddenConnections, expandedNode, visibleData]);

  const options: Highcharts.Options = {
    chart: {
      type: 'sankey',
      spacingTop: 25,
      spacingBottom: 25,
      spacingLeft: 25,
      spacingRight: 25,
      events: {
        render(this: Highcharts.Chart) {
          setChart(this);
        },
        redraw(this: Highcharts.Chart) {
          setChart(this);
        },
      },
    },
    xAxis: {
      scrollbar: {
        enabled: false,
      },
    },
    rangeSelector: {
      enabled: false,
    },
    navigator: {
      enabled: false,
    },
    plotOptions: {
      sankey: {
        events: {
          click(e) {
            const point = e.point as CustomNodePoint;
            const meterId = point?.custom?.meterId;
            if (meterId) {
              const meterName = e.point.name;
              nodeOnClick({ meterId, meterName });
            }
          },
        },
        linkColorMode: 'to',
        minLinkWidth: 1,
        nodeWidth: 35,
        nodeAlignment: 'center',
        nodePadding: 15,
        tooltip: {
          nodeFormatter() {
            const value = this as any;
            let displayValue = value.sum;

            // For GRID nodes, use the incoming SOURCE link weight instead of Highcharts sum
            if (
              value.meter_category === 'GRID' ||
              value.meter_category === 'GRID_INPUT'
            ) {
              const sourceLink = visibleData.find(
                (link: any) =>
                  link.to === value.id && link.meter_category === 'SOURCE'
              );
              if (sourceLink) {
                displayValue = sourceLink.weight;
              }
            }

            // For SOURCE nodes (SOLAR, WIND, CHP), use the outgoing link weight
            if (['SOLAR', 'WIND', 'CHP'].includes(value.meter_category)) {
              const outgoingLink = visibleData.find(
                (link: any) =>
                  link.from_node === value.id &&
                  link.meter_category === value.meter_category
              );
              if (outgoingLink) {
                displayValue = outgoingLink.weight;
              }
            }

            // For TOTAL CONSUMPTION nodes, use the actual energy from backend
            if (value.meter_category === 'TOTAL CONSUMPTION') {
              if (value.actualEnergy !== undefined) {
                displayValue = value.actualEnergy;
              }
            }

            // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
            return `<b>${value.meter_category}:</b></br>
          ${formatDecimalSeparator({
            value: displayValue,
            decimalScale: 2,
          })} ${unit}
            `;
          },
          pointFormatter() {
            const point = this as unknown as SankeyPoint;

            return point?.fromNode && point?.toNode && point?.weight && point
              ? `<b>${point.fromNode.name}</b> → <b>${
                  point.toNode.name
                }</b> <br>
                <b>${point.fromNode.meter_category}</b> → <b>${
                  point.toNode.meter_category
                }</b> <br>
                ${formatDecimalSeparator({
                  value: point.weight,
                  decimalScale: 2,
                })} ${unit}`
              : `<b>${point?.name ?? ''}</b>: <br>${point?.sum ?? 0} ${unit}`;
          },
        },
      },
    },
    tooltip: {
      useHTML: true,
      backgroundColor: theme.colors['gray-25'],
    },

    series: [
      {
        data: visibleData ?? [],
        type: 'sankey',
        name: 'Flow',
        linkOpacity: 0.25,
        curveFactor: 0.4,
        nodes: chartNodes,
        dataLabels: {
          align: 'right',
          x: -40,
          useHTML: true,
          nodeFormatter() {
            const point = this.point as any;
            const isLeftSide = point.column === 0;

            if (isLeftSide) {
              // Left side - align text to the right of the node
              return `<span style="position: relative; left: 40px; text-align: left;">${
                this.key as string
              }</span>`;
            } else {
              // Right side - align text to the left of the node
              return `<span style="position: relative; right: 0px; text-align: right;">${
                this.key as string
              }</span>`;
            }
          },
        },
      },
    ],
  };

  useEffect(() => {
    if (!chart) {
      return;
    }

    const updatePositions = (): void => {
      const hasChildrenPositions: Record<string, { x: number; y: number }> = {};
      const nodePositions: Record<string, { x: number; y: number }> = {};
      const series = chart?.series?.[0];
      if (!series) return;

      (series as any).nodes.forEach((node: any) => {
        const nodeHeight = node.shapeArgs?.height ?? 1;
        // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
        const x = node.nodeX + chart.plotLeft;
        // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
        const y = node.nodeY + chart.plotTop + nodeHeight / 2;

        nodePositions[node.id] = { x, y };
        if (!hiddenConnections?.[node.id]) {
          return;
        }

        hasChildrenPositions[node.id] = { x, y };
      });

      setNodePositions(hasChildrenPositions);
      setEveryNodePositions(nodePositions);
    };
    setTimeout(updatePositions, 0);

    (chart as any)?.addEvent?.('redraw', updatePositions);
    return () => {
      if (chart) {
        (chart as any)?.removeEvent?.('redraw', updatePositions);
      }
    };
  }, [chart, hiddenConnections, expandedNode]);

  return (
    <div className="relative w-full h-96" id="building-sankey">
      <HChart options={options} modules={[SankeyModule]} callback={setChart} />
      {chartNodes?.length > 0 &&
        everyNodePositions &&
        chartNodes.map((node) => {
          const nodePosition = everyNodePositions?.[node.id];
          if (!nodePosition || !node.percentage) return <></>;

          return (
            <div
              key={node.id}
              className="absolute transform -translate-y-1/2 rounded-full p-1 pointer-events-none"
              style={{
                left: `${nodePosition.x - 3}px`,
                top: `${nodePosition.y}px`,
                zIndex: 1,
              }}
            >
              <div className="w-[33px] h-[14px] text-[10px] font-bold bg-white flex items-center rounded-sm justify-evenly">
                <p>
                  {convertToPercentageParsedString(Math.abs(node.percentage), {
                    decimalScale: 1,
                    clampPercentage: false,
                  })}
                </p>
                {node.color === theme.colors.success ? (
                  <PiArrowDownFill fill={node.color} size={14} />
                ) : (
                  <PiArrowUpFill fill={node.color} size={14} />
                )}
              </div>
            </div>
          );
        })}
      {Object.entries(nodePositions).map(([nodeId, position]) => {
        // Don't show expand button for nodes that are children of the expanded node
        // This prevents confusing nested expansions with dual-parent hierarchies
        const isChildOfExpandedNode =
          expandedNode &&
          hiddenConnections?.[expandedNode]?.some(
            (link: any) => link.to === nodeId
          );

        if (isChildOfExpandedNode) {
          return null;
        }

        return (
          <button
            key={nodeId}
            className="absolute transform -translate-y-1/2 rounded-full p-1"
            style={{
              left: `${position.x + 35}px`,
              top: `${position.y}px`,
              zIndex: 1000,
            }}
            onClick={() => {
              handleExpandNode(nodeId);
            }}
            title={`${
              expandedNode === nodeId ? 'Collapse' : 'Expand'
            } ${nodeId}`}
          >
            <div
              className={`w-[20px] h-[20px] leading-none border flex items-center justify-center rounded-full bg-white shadow-lg transition-transform`}
            >
              {expandedNode === nodeId ? <MdOutlineRemove /> : <BsThreeDots />}
            </div>
          </button>
        );
      })}
    </div>
  );
};
export default BuildingSankeyChart;
